import { type ShiftId } from "@clipboard-health/contract-backend-main";
import { zodResolver } from "@hookform/resolvers/zod";
import useSendbirdStateContextHook from "@sendbird/uikit-react/useSendbirdStateContext";
import { BACKEND_MAIN_ERROR_CODES } from "@src/appV2/api/code";
import { isCbhApiAxiosError } from "@src/appV2/Errors/errorUtils";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useConfirmAttendance } from "@src/appV2/Shifts/UrgentShifts/api/useConfirmAttendance";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { formatISO, parseISO } from "date-fns";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { generatePath, useHistory } from "react-router-dom";
import { z } from "zod";

import { SHIFT_DETAILS_UPDATE_ETA_CONFIRMED_FULL_PATH } from "../../../WorkerShifts/path";
import { invalidateWorkerTrips } from "../useGetWorkerTrips";
import { UpdateEtaBottomSheet } from "./BottomSheet";
import { UPDATE_ETA_BOTTOM_SHEET_SOURCE, type UpdateEtaBottomSheetSource } from "./types";
import { getAutoMessageToFacility } from "./utils/getAutoMessageToFacility";
import { isEtaInOptions } from "./utils/isEtaInOptions";
import { sendMessageToFacility } from "./utils/sendMessageToWorkplace";

const updateEtaSchema = z.object({
  arrivalTimeIso: z.string().min(1, "Please select an arrival time"),
  message: z.string(),
});

type UpdateEtaFormValues = z.infer<typeof updateEtaSchema>;

export interface UpdateEtaBottomSheetFormProps {
  modalState: UseModalState;
  shiftId: string;
  workplaceId: string;
  workplaceTimezone: string;
  shiftStartTime: Date;
  etaUpdateAvailableTimes: Date[];
  source?: UpdateEtaBottomSheetSource;
}

export function UpdateEtaBottomSheetForm(props: UpdateEtaBottomSheetFormProps) {
  const {
    modalState,
    shiftId,
    workplaceId,
    workplaceTimezone,
    shiftStartTime,
    etaUpdateAvailableTimes,
    source: sourceProperty,
  } = props;

  const { source: sourceQueryParameter } = useSearchParams<{
    source?: UpdateEtaBottomSheetSource;
  }>();

  const source = sourceQueryParameter ?? sourceProperty ?? UPDATE_ETA_BOTTOM_SHEET_SOURCE.DEEP_LINK;

  const history = useHistory();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const sendBirdStateContext = useSendbirdStateContextHook();
  const worker = useDefinedWorker();

  const { mutateAsync: confirmAttendance, isLoading: isConfirmAttendanceLoading } =
    useConfirmAttendance();

  // eslint-disable-next-line prefer-destructuring
  const initialEta = etaUpdateAvailableTimes[0];
  const initialArrivalTimeIso = formatISO(initialEta);
  const initialMessage = getAutoMessageToFacility({
    selectedEta: initialEta,
    shiftStartTime,
    workplaceTimezone,
  });

  const { handleSubmit, setValue, watch } = useForm<UpdateEtaFormValues>({
    resolver: zodResolver(updateEtaSchema),
    defaultValues: {
      arrivalTimeIso: initialArrivalTimeIso,
      message: initialMessage,
    },
  });

  const arrivalTimeIso = watch("arrivalTimeIso");
  const message = watch("message");

  const selectedEta = useMemo(() => parseISO(arrivalTimeIso), [arrivalTimeIso]);

  const onSubmit = handleSubmit(async (data: UpdateEtaFormValues) => {
    if (!isEtaInOptions(data.arrivalTimeIso, etaUpdateAvailableTimes)) {
      showErrorToast("Please select a valid arrival time");
      logError(APP_V2_APP_EVENTS.UPDATE_ETA_INVALID_SELECTION, {
        error: new Error("Submitted arrivalTimeIso is not in etaUpdateAvailableTimes"),
        metadata: {
          shiftId,
          workplaceId,
          attempted: data.arrivalTimeIso,
          options: etaUpdateAvailableTimes.map((time) => time.toISOString()),
          source,
        },
      });
      return;
    }

    try {
      const selectedTime = parseISO(data.arrivalTimeIso);

      const trimmedMessage = data.message.trim();
      const messageToFacility =
        trimmedMessage.length > 0
          ? trimmedMessage
          : getAutoMessageToFacility({
              selectedEta: selectedTime,
              shiftStartTime,
              workplaceTimezone,
            });

      await confirmAttendance({
        data: {
          type: "attendance-confirmation",
          attributes: {
            etaAt: data.arrivalTimeIso,
          },
          relationships: {
            shift: {
              data: {
                type: "shift",
                id: shiftId as ShiftId,
              },
            },
          },
        },
      });

      await sendMessageToFacility({
        messageToFacility,
        sendBirdStateContext,
        workplaceId,
        workerId: worker.userId,
      });

      logEvent(APP_V2_USER_EVENTS.SUBMITTED_ETA_MODAL, {
        shiftId,
        workerId: worker.userId,
        facilityId: workplaceId,
        shiftStart: shiftStartTime.toISOString(),
        etaSubmitted: data.arrivalTimeIso,
        messageToFacility,
        source,
        version: "v2",
      });

      await invalidateWorkerTrips(queryClient, worker.userId, shiftId);

      history.replace(
        generatePath(SHIFT_DETAILS_UPDATE_ETA_CONFIRMED_FULL_PATH, {
          shiftId,
        }),
        {
          updatedEta: selectedTime.toISOString(),
          workplaceTimezone,
        }
      );
    } catch (error) {
      if (isCbhApiAxiosError(error)) {
        const isShiftNoLongerAvailable = error.response.data.errors.some(
          (apiError) => apiError.code === BACKEND_MAIN_ERROR_CODES.shiftAssignedToAnotherWorker
        );

        if (isShiftNoLongerAvailable) {
          showErrorToast("This shift is no longer available");
          history.replace("/home-v2/shift-discovery/list");
          return;
        }
      }

      showErrorToast("Something went wrong while updating your ETA. Please try again.");
      logError(APP_V2_APP_EVENTS.UPDATE_ETA_BOTTOM_SHEET_ERROR, {
        error,
        metadata: {
          shiftId,
          workplaceId,
        },
      });
    }
  });

  return (
    <UpdateEtaBottomSheet
      modalState={modalState}
      isSubmitting={isConfirmAttendanceLoading}
      shiftStartTime={shiftStartTime}
      selectedEta={selectedEta}
      workplaceTimezone={workplaceTimezone}
      etaUpdateAvailableTimes={etaUpdateAvailableTimes}
      message={message}
      onEtaChange={(eta: Date): void => {
        setValue("arrivalTimeIso", formatISO(eta));
        setValue(
          "message",
          getAutoMessageToFacility({
            selectedEta: eta,
            shiftStartTime,
            workplaceTimezone,
          })
        );
      }}
      onMessageChange={(nextMessage) => {
        setValue("message", nextMessage);
      }}
      onSubmit={async () => {
        await onSubmit();
      }}
    />
  );
}
